import { ErroreClientItem } from './error-reporting.models';
import { OFFLINE_QUEUE_MAX, OFFLINE_QUEUE_STORAGE_KEY } from './error-reporting.constants';

/**
 * Un elemento della coda offline con l'identità di chi lo ha generato (CORREZIONI-GIRO-2,
 * "Mobile", punto 2 — "Identità"): `sub` è il claim del JWT presente al momento in cui l'elemento
 * è stato GENERATO (non quello del token al momento dell'invio), `null` se generato da anonimo.
 * Viaggia con l'elemento proprio perché la coda sopravvive a logout/login e riavvii: senza,
 * l'errore di un utente A verrebbe attribuito al token B presente al momento del flush.
 */
export interface OfflineQueueEntry {
  item: ErroreClientItem;
  sub: string | null;
}

/**
 * Coda offline persistita del reporter (SPEC "Mobile", "Coda offline"): il buffer non inviato
 * (rete assente/invio fallito) sopravvive al reload/riavvio dell'app finché non si svuota al
 * boot, al ritorno della rete o dell'app in primo piano. Max {@link OFFLINE_QUEUE_MAX} elementi,
 * i più recenti: oltre il tetto si scartano i più vecchi.
 *
 * Funzioni pure e difensive: qualsiasi errore di `localStorage` (quota superata, contesto
 * privato) degrada in silenzio — la coda offline è best-effort, non deve mai propagare.
 */

export function readOfflineQueue(): OfflineQueueEntry[] {
  try {
    const raw = localStorage.getItem(OFFLINE_QUEUE_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed
      .map((entry: unknown) => normalizeEntry(entry))
      .filter((entry): entry is OfflineQueueEntry => entry !== null);
  } catch {
    return [];
  }
}

/**
 * Una coda scritta da una versione precedente (prima dell'identità per elemento) conteneva
 * `ErroreClientItem` grezzi, senza `sub`. Trattarli come anonimi è la scelta più sicura: non c'è
 * modo di risalire a chi li aveva generati, e non si deve MAI attribuirli al token corrente.
 */
function normalizeEntry(entry: unknown): OfflineQueueEntry | null {
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const record = entry as Record<string, unknown>;
  if ('item' in record) {
    const item = record['item'];
    if (!item || typeof item !== 'object') {
      return null;
    }
    const sub = record['sub'];
    return { item: item as ErroreClientItem, sub: typeof sub === 'string' ? sub : null };
  }
  if ('origine' in record) {
    // Formato pregresso: elemento grezzo, nessuna identità nota.
    return { item: record as unknown as ErroreClientItem, sub: null };
  }
  return null;
}

export function clearOfflineQueue(): void {
  try {
    localStorage.removeItem(OFFLINE_QUEUE_STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/** Accoda in coda la lista data, troncando ai {@link OFFLINE_QUEUE_MAX} elementi più recenti. */
export function enqueueOfflineItems(entries: OfflineQueueEntry[]): void {
  if (!entries?.length) {
    return;
  }
  try {
    const merged = [...readOfflineQueue(), ...entries];
    const trimmed = merged.length > OFFLINE_QUEUE_MAX
      ? merged.slice(merged.length - OFFLINE_QUEUE_MAX)
      : merged;
    localStorage.setItem(OFFLINE_QUEUE_STORAGE_KEY, JSON.stringify(trimmed));
  } catch {
    // best-effort
  }
}
